import { useAuth } from '@shared/auth';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormButton,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { APP_PERMISSIONS } from '@user-management/permissions';
import { useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useGetUser, useUserDetailsFormLogic, useUserDetailsFormSubmission } from '../../hooks';
import { getUserDetailsFormSchema } from '../../schema';
import { type UserDetailsFormValues } from '../../types';
import { UserDetailsFormModalContent } from '../user-details-form-modal-content';
import { UserUpdateErrorsAlert } from '../user-update-errors-alert';

interface UserDetailsFormModalProps {
  id: string;
  open: boolean;
  onClose: () => void;
}

export default function UserDetailsFormModal(props: Readonly<UserDetailsFormModalProps>) {
  const { id: userId, open: isOpen, onClose } = props;

  const { data: user, loading: isLoading, error } = useGetUser(userId);
  const { userProfile } = useAuth();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    if (modalContentRef.current) {
      modalContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isOwner = userProfile?.id === userId;

  const hasEditStatusPermission = useHasPermission(APP_PERMISSIONS.SETTING.USER.STATUS);

  const formSchema = useMemo(() => {
    return getUserDetailsFormSchema(isOwner, user?.status, hasEditStatusPermission);
  }, [isOwner, user?.status, hasEditStatusPermission]);

  const { showPromiseSnackbar } = useSnackbar();

  const formContext = useForm<UserDetailsFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
    },
    resolver: zodResolver(formSchema),
  });

  const handleClose = () => {
    if (formContext.formState.isSubmitting) {
      return;
    }
    onClose();
  };

  const { disabledFields } = useUserDetailsFormLogic(formContext, user, isOwner);
  const {
    handleSubmit: handleFormSubmission,
    errors,
    loading: isSubmissionLoading,
  } = useUserDetailsFormSubmission({
    formContext,
    user,
    onCompleted: handleClose,
    onError: scrollToTop,
  });

  const onSubmit = async (data: UserDetailsFormValues) => {
    showPromiseSnackbar({
      promise: async () => {
        await handleFormSubmission(data);
      },
      message: {
        pending: 'Updating user details',
        success: 'User details updated successfully',
        error: 'Failed to update user details',
      },
    });
  };

  const shouldDisableFormSubmission = useMemo(
    () =>
      Object.values(disabledFields).every((field) => field) ||
      isLoading ||
      formContext.formState.isSubmitting ||
      isSubmissionLoading,
    [disabledFields, isLoading, formContext.formState.isSubmitting, isSubmissionLoading],
  );

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: {
            component: Form,
            onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
              e.stopPropagation();
              formContext.handleSubmit(onSubmit)(e);
            },
          },
        }}
      >
        <DialogTitle onClose={handleClose}>User Details</DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(6) }}
          ref={modalContentRef}
        >
          <UserUpdateErrorsAlert errors={errors} />
          <UserDetailsFormModalContent
            loading={isLoading}
            error={error}
            data={user}
            disabledFields={disabledFields}
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant='outlined'
            color='secondary'
            size='large'
            onClick={handleClose}
            disabled={formContext.formState.isSubmitting}
          >
            Cancel
          </Button>
          <FormButton label='Save' size='large' disabled={shouldDisableFormSubmission} />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
